import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { Grid, Box, Typography, LinearProgress, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';
import base64 from 'base-64';
import FileSaver from 'file-saver';
import { useHistory } from 'react-router-dom';
import TextAreaCopy from './text-area-copy';
import ParsedData from './parsed-data';
import { AppState } from '../../../store';
import Transactions from '../../../services/transactions';
import MessageParser from '../../../services/message-parser';
import GetCommandById from '../../../services/commands';
import { Command } from '../../../entities/commands-v1/command';
import { QueryParameterKeys } from '../../../globals/paramter';
import CommandPayload from './command-payload';
import { uniqueId } from '../../../globals/utils';
import ParsedJsonData from './parsed-json-data';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { expandableData } from './row-details-cell';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import { getParserDetails } from './calls';
import { setRenderringSubComponent } from '../../../store/transactions/actions';
import DealerInfo from './dealer-info';
import { transactionsActionNames } from '../../../store/transactions/types';

type AccordionProperties =
  | any
  | {
    row: any;
    id?: number;
    handleExpandedHeightsChange?: Function;
  };

type ParsedMessage = {
  applicationBaseTsfV0Json?: string;
  applicationBaseIntermediateTSfV0Json?: string;
  applicationXml?: string;
};

type PayloadData = {
  rawPayload: string;
  parsedMessage?: ParsedMessage;
  payloadFormat: string;
};
interface CommandWithHash extends Command {
  hash?: string;
}
const PLMESSAGE = 'MESSAGE';
const COMMAND = 'COMMAND';
const DATAFILE = 'DATAFILE';

const RowDetails: FC<AccordionProperties> = ({
  row: originalRow,
  id: rowNumber,
  handleExpandedHeightsChange: setExpandedHeights,
}: AccordionProperties) => {
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const detailContainer: RefObject<HTMLDivElement> = useRef(null);
  const history = useHistory();
  const parameters: URLSearchParams = new URLSearchParams(history.location.search);
  const dealerCode = parameters.get('dealerCode') || null;
  const vimsData = parameters.get(QueryParameterKeys.vimsData) || undefined;
  const vimsTransaction = parameters.get('vimsTransaction') || null;
  const deviceId = useSelector((state: AppState) => state.search.selectedEntity?.deviceId);
  const deviceType = useSelector((state: AppState) => state.search.selectedEntity?.deviceType);
  const deviceKey = useSelector((state: AppState) => state.search.selectedEntity?.deviceKey);
  const telematicsAssetId = useSelector((state: AppState) => !vimsTransaction ? state.search.selectedEntity?.telematicsAssetId : state.search.selectedVIMSEntity?.telematicsAssetId);
  const [row, setRow] = useState(originalRow);
  const [hexPayload, setHexPayload] = useState<string>();
  const [loadingDetails, setLoadingDetails] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [command, setCommand] = useState<any>();
  const [updateHeight, setUpdateHeight] = useState(uniqueId());
  const [transactionDetails, setTransactionDetails] = useState<any>();
  const [payloadData, setPayloadData] = useState<PayloadData>();
  const [height, setHeight] = useState(0);
  const transactionsService = new Transactions(client, envVariables.transactionsV1BaseUrl);
  const messageParserService = new MessageParser(client, envVariables.parsedMessageV1BaseUrl);
  const commandService = new GetCommandById(client, envVariables.commandsV1BaseUrl);
  const configFilesLibrary = new ConfigurationFilesLibrary(
    client,
    envVariables.configurationFilesLibraryV1BaseUrl,
  );
  const dispatch = useDispatch();
  // eslint-disable-next-line unicorn/prefer-set-has
  const exceptFileTypeIds = ['10262', '11018', '11019', '10546', '10548'];
  const { id } = row;
  const deviceKeyRow = row.deviceKey;
  const setLoadingFlag = (flag: boolean) => {
    dispatch({
      type: transactionsActionNames.SET_LOADING_FLAG,
      payload: flag,
    });
  };
  const updateHeightOfComponent = () => {
    dispatch(setRenderringSubComponent(true));
    setLoadingFlag(true);
    setTimeout(() => {
      setUpdateHeight(uniqueId());
      dispatch(setRenderringSubComponent(false));
      setLoadingFlag(false);
    }, 100);
  };
  useEffect(() => {
    setLoadingFlag(true);
    setTimeout(() => {
      setLoadingFlag(false);
    }, 200);
  }, []);

  const getRawMessage = (payloadData: any, fileTypeId: string): any => {
    if (exceptFileTypeIds.includes(fileTypeId) || payloadData?.payloadFormat === 'xml') {
      return payloadData?.rawPayload;
    }
    return payloadData?.payloadFormat === 'binary'
      ? payloadData?.rawPayload
      : base64.encode(payloadData?.rawPayload);
  };

  const fetchParsingDetails = async () => {
    const { fileTypeId } = row;
    if (
      payloadData?.rawPayload &&
      !payloadData?.parsedMessage &&
      deviceType &&
      deviceId &&
      (payloadData?.payloadFormat !== 'json' || exceptFileTypeIds.includes(fileTypeId))
    ) {
      setLoadingDetails('Parsing Message...');
      updateHeightOfComponent();
      try {
        const parsedMessageResponse: AxiosResponse<any> =
          await messageParserService.getParsedMessage({
            deviceId,
            deviceType,
            fileTypeId: row.fileTypeId,
            rawMessage: getRawMessage(payloadData, fileTypeId),
            messageFormat: payloadData?.payloadFormat,
            messageId: row.id,
            sourceType: transactionDetails.sourceType,
            gatewayNameAndRegion: transactionDetails.gatewayNameAndRegion,
            messageProtocol: transactionDetails.messageProtocol,
            gatewayReceiptTime: transactionDetails.gatewayReceiptTime,
            metadata: { messageSourceId: transactionDetails.messageSourceId },
          });
        const parsedDataObject = getParserDetails(parsedMessageResponse);
        setLoadingDetails(null);
        setPayloadData({ ...payloadData, parsedMessage: parsedDataObject });
        updateHeightOfComponent();
      } catch (error_: any) {
        setError(true);
        if (error_?.response?.data?.message?.toLowerCase().includes('unsupported content')) {
          setLoadingDetails(null);
          setPayloadData({ ...payloadData, parsedMessage: {} });
        } else {
          setLoadingDetails('Sorry there has been Error parsing your message');
        }
      }
    }
  };
  const fetchTransactionDetails = async () => {
    if (row && dealerCode) {
      try {
        const transactionDetailsResponse: AxiosResponse<any> = await transactionsService.getDetails(
          {
            id: row.id,
            deviceKey: row.key,
          },
        );
        setTransactionDetails(transactionDetailsResponse.data.transaction);
      } catch (error) {
        dispatch(
          enqueueSnackbarAction({
            message: error.message,
            options: {
              variant: 'error',
            },
          }),
        );
      }
    }
    if (row && (row.transactionType === PLMESSAGE && row.deviceKey) || (row.transactionType === DATAFILE && row.isProtoParsingSupported === true)) {
      setLoadingDetails('Loading Details...');
      updateHeightOfComponent();
      const transactionDetailsResponse: AxiosResponse<any> = await transactionsService.getDetails({
        id: row.id,
        deviceKey: row.deviceKey,
      });
      setTransactionDetails(transactionDetailsResponse.data.transaction);
      if (transactionDetailsResponse.data.transaction.rawPayloadFormat === 'hexadecimal')
        setHexPayload(transactionDetailsResponse.data.transaction.hexPayload);
      setLoadingDetails(null);
      setPayloadData({
        rawPayload: transactionDetailsResponse.data.transaction.rawPayload,
        payloadFormat:
          transactionDetailsResponse.data.transaction.rawPayloadFormat === 'hexadecimal'
            ? 'binary'
            : transactionDetailsResponse.data.transaction.rawPayloadFormat,
        parsedMessage: undefined,
      });
    } else if (row && row.transactionType === COMMAND && !command && !loadingDetails && row.id) {
      setLoadingDetails('Loading Command...');
      const commandDetails: AxiosResponse<CommandWithHash> = await commandService.getCommandById({
        commandId: row.id,
      });
      setCommand(commandDetails.data);
      updateHeightOfComponent();
      if (commandDetails.data.hash) {
        try {
          const libraryResponse = await configFilesLibrary.getConfigFileMetaData(
            commandDetails.data.hash,
          );
          setRow({ ...row, fileTypeName: libraryResponse.data.fileName });
        } catch (error_: any) {
          if (!error_.message.includes('404')) {
            dispatch(
              enqueueSnackbarAction({
                message: error_.message,
                options: {
                  variant: 'error',
                },
              }),
            );
          }
        }
      }
      updateHeightOfComponent();
      setLoadingDetails(null);
    }
  };
  useEffect(() => {
    if (!transactionDetails) fetchTransactionDetails();
  }, [id, deviceKeyRow]);

  useEffect(() => {
    if (payloadData) fetchParsingDetails();
  }, [payloadData]);

  useEffect(() => {
    const newHeight = detailContainer?.current?.offsetHeight;
    if (setExpandedHeights && rowNumber && newHeight && newHeight !== height) {
      setHeight(newHeight);
      setExpandedHeights({
        [rowNumber]: detailContainer?.current?.offsetHeight,
      });
    }
  }, [updateHeight]);

  const downloadDataFile = async (response: any) => {
    if (response.data.type === 'application/octet-stream') {
      FileSaver.saveAs(
        response.data,
        `${row.fileName || row.fileTypeName}${row.extension ? `.${row.extension}` : ''}`,
      );
    } else {
      response.data
        .text()
        .then((url: any) => {
          window.location = url;
          return url;
        })
        .catch((error: any) => {
          throw new Error(error);
        });
    }
  };

  const downloadDataFileTransaction = async () => {
    if (deviceKey || dealerCode) {
      if (vimsData) {
        const key = dealerCode ? row.key : telematicsAssetId;
        const response = await transactionsService.downloadVIMSDataFileTransaction(row.id, key);
        downloadDataFile(response);
      } else {
        const key = dealerCode ? row.key : deviceKey;
        const response = await transactionsService.downloadDataFileTransaction(row.id, key);
        downloadDataFile(response);
      }
    } else {
      const key = dealerCode ? row.key : telematicsAssetId;
      const response = await transactionsService.downloadVIMSDataFileTransaction(row.id, key);
      downloadDataFile(response);
    }
  };

  return (
    <div ref={detailContainer}>
      <Box id={`device-detail-${row.id}`}>
        <Grid container p={4}>
          <Grid item xs={row.transactionType === DATAFILE ? 10 : 12} m={0}>
            <Grid container spacing={3}>
              {expandableData.map((data: any) => (
                <Grid key={data.name} item sm={12} md={6} lg={4} style={{ padding: 2 }}>
                  <Box display="flex" flexDirection="row">
                    <Box mr={1}>
                      <Typography style={{ fontWeight: 'bold' }} variant="body1">{data.title}: </Typography>
                    </Box>
                    <Box>
                      <Typography>{data.getCellValue(row)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}

              {dealerCode && loadingDetails && !payloadData?.parsedMessage && (
                <Box
                  id={`loading-details-${row.id}`}
                  display="flex"
                  alignItems="center"
                  width="100%"
                  p={2}
                  pl={0}
                  pr={4}
                >
                  <Box pr={1}>
                    <Typography style={{ whiteSpace: 'nowrap' }}>{loadingDetails}</Typography>
                  </Box>
                  {!error && <LinearProgress color="secondary" style={{ width: '1000%' }} />}
                </Box>
              )}
              {row.transactionType === PLMESSAGE && (
                <Grid item xs={12} style={{ padding: '1rem 2rem 0 0' }}>
                  <Box display="flex" flexDirection="column" width="100%">
                    {payloadData?.parsedMessage && payloadData?.parsedMessage?.applicationXml && (
                      <>
                        <Box id={`parsed-data-${row.id}`}>
                          <ParsedData
                            updateHeight={updateHeightOfComponent}
                            parsedMessage={JSON.stringify(
                              payloadData?.parsedMessage?.applicationXml,
                            )}
                          />
                        </Box>
                        <Box>
                          <TextAreaCopy
                            updateHeight={updateHeightOfComponent}
                            title="Parsed XML"
                            content={JSON.stringify(payloadData?.parsedMessage?.applicationXml)}
                            contentType="xml"
                          />
                        </Box>
                      </>
                    )}
                    {payloadData?.parsedMessage?.applicationBaseIntermediateTSfV0Json && (
                      <Box id={`json-${row.id}`}>
                        <ParsedJsonData
                          updateHeight={updateHeightOfComponent}
                          parsedJSON={
                            payloadData?.parsedMessage?.applicationBaseIntermediateTSfV0Json
                          }
                        />
                      </Box>
                    )}
                    {payloadData?.parsedMessage?.applicationBaseTsfV0Json && (
                      <Box id={`json-${row.id}`}>
                        <TextAreaCopy
                          updateHeight={updateHeightOfComponent}
                          title="JSON"
                          content={JSON.stringify(
                            payloadData?.parsedMessage.applicationBaseTsfV0Json,
                          )}
                          contentType="json"
                        />
                      </Box>
                    )}
                    {payloadData?.rawPayload && (
                      <Box id={`raw-message${row.id}`}>
                        <TextAreaCopy
                          updateHeight={updateHeightOfComponent}
                          title="Raw Payload"
                          content={payloadData?.rawPayload}
                          contentType={payloadData?.payloadFormat}
                        />
                      </Box>
                    )}
                    {hexPayload && (
                      <Box id={`hex-payload-${row.id}`}>
                        <TextAreaCopy
                          updateHeight={updateHeightOfComponent}
                          title="HEX Payload"
                          content={hexPayload}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
              {row.transactionType === DATAFILE && row.isProtoParsingSupported === true && (
                <Grid item xs={12} style={{ padding: '1rem 2rem 0 0' }}>
                  <Box display="flex" flexDirection="column" width="100%">
                    {payloadData?.parsedMessage && payloadData?.parsedMessage?.applicationXml && (
                      <>
                        <Box id={`parsed-data-${row.id}`}>
                          <ParsedData
                            updateHeight={updateHeightOfComponent}
                            parsedMessage={JSON.stringify(
                              payloadData?.parsedMessage?.applicationXml,
                            )}
                          />
                        </Box>
                        <Box>
                          <TextAreaCopy
                            updateHeight={updateHeightOfComponent}
                            title="Parsed XML"
                            content={JSON.stringify(payloadData?.parsedMessage?.applicationXml)}
                            contentType="xml"
                          />
                        </Box>
                      </>
                    )}
                    {payloadData?.parsedMessage?.applicationBaseIntermediateTSfV0Json && (
                      <Box id={`json-${row.id}`}>
                        <ParsedJsonData
                          updateHeight={updateHeightOfComponent}
                          parsedJSON={
                            payloadData?.parsedMessage?.applicationBaseIntermediateTSfV0Json
                          }
                        />
                      </Box>
                    )}
                    {payloadData?.parsedMessage?.applicationBaseTsfV0Json && (
                      <Box id={`json-${row.id}`}>
                        <TextAreaCopy
                          updateHeight={updateHeightOfComponent}
                          title="JSON"
                          content={JSON.stringify(
                            payloadData?.parsedMessage.applicationBaseTsfV0Json,
                          )}
                          contentType="json"
                        />
                      </Box>
                    )}
                    {payloadData?.rawPayload && (
                      <Box id={`raw-message${row.id}`}>
                        <TextAreaCopy
                          updateHeight={updateHeightOfComponent}
                          title="Raw Payload"
                          content={payloadData?.rawPayload}
                          contentType={payloadData?.payloadFormat}
                        />
                      </Box>
                    )}
                    {hexPayload && (
                      <Box id={`hex-payload-${row.id}`}>
                        <TextAreaCopy
                          updateHeight={updateHeightOfComponent}
                          title="HEX Payload"
                          content={hexPayload}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
              {row.transactionType === COMMAND && command && (
                <Grid item xs={12} style={{ padding: '1rem 2rem 1rem 0' }}>
                  <CommandPayload
                    updateHeight={updateHeightOfComponent}
                    command={command}
                    downloadFile={downloadDataFileTransaction}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          {row.transactionType === DATAFILE && (
            <Grid item xs={2}>
              <Box display="flex" justifyContent="center">
                <Button variant="outlined" onClick={downloadDataFileTransaction}>
                  <Typography variant="button">Download</Typography>
                </Button>
              </Box>
            </Grid>
          )}
          {dealerCode && (
            <Grid item xs={12}>
              <Box id={`dealer-info-${row.id}`}>
                <DealerInfo
                  title="Information"
                  updateHeight={updateHeightOfComponent}
                  row={transactionDetails}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </div>
  );
};

export default RowDetails;
